import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Loader2, CheckCircle2, XCircle, Clock, Image, ExternalLink, Coins, Filter, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PaymentManagementPage() {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("pending");
  const [actionLoading, setActionLoading] = useState({});
  const [rejectReasons, setRejectReasons] = useState({});
  const [showRejectForm, setShowRejectForm] = useState({});
  const [screenshotUrls, setScreenshotUrls] = useState({});

  const fetchSubmissions = async (statusFilter) => {
    setLoading(true);
    try {
      const params = statusFilter !== "all" ? `?status=${statusFilter}` : "";
      const res = await axios.get(`${API}/admin/payments${params}`);
      setSubmissions(res.data.submissions || []);
    } catch (e) {
      toast.error("Failed to load payments");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSubmissions(filter);
  }, [filter]);

  const handleApprove = async (id) => {
    setActionLoading((p) => ({ ...p, [id]: "approve" }));
    try {
      const res = await axios.post(`${API}/admin/payments/${id}/approve`);
      toast.success(res.data.message);
      fetchSubmissions(filter);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Approve failed");
    } finally {
      setActionLoading((p) => ({ ...p, [id]: null }));
    }
  };

  const handleReject = async (id) => {
    setActionLoading((p) => ({ ...p, [id]: "reject" }));
    try {
      const res = await axios.post(`${API}/admin/payments/${id}/reject`, {
        reason: rejectReasons[id] || "",
      });
      toast.success(res.data.message);
      setShowRejectForm((p) => ({ ...p, [id]: false }));
      fetchSubmissions(filter);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reject failed");
    } finally {
      setActionLoading((p) => ({ ...p, [id]: null }));
    }
  };

  const loadScreenshot = async (sub) => {
    if (screenshotUrls[sub.id]) return;
    try {
      const res = await axios.get(`${API}/billing/payment-screenshot`, {
        params: { path: sub.screenshot_path },
        responseType: "blob",
      });
      const blobUrl = URL.createObjectURL(res.data);
      setScreenshotUrls((p) => ({ ...p, [sub.id]: blobUrl }));
    } catch {
      toast.error("Failed to load screenshot");
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "pending":
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-500/10 text-amber-400 border border-amber-500/20">
            <Clock className="h-2.5 w-2.5" /> PENDING
          </span>
        );
      case "approved":
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-green-500/10 text-green-400 border border-green-500/20">
            <CheckCircle2 className="h-2.5 w-2.5" /> APPROVED
          </span>
        );
      case "rejected":
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-red-500/10 text-red-400 border border-red-500/20">
            <XCircle className="h-2.5 w-2.5" /> REJECTED
          </span>
        );
      default:
        return null;
    }
  };

  const pendingCount = submissions.filter((s) => s.status === "pending").length;

  return (
    <div className="space-y-5" data-testid="payment-management-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-100" style={{ fontFamily: "Outfit, sans-serif" }}>
            Payment Requests
          </h1>
          <p className="text-xs sm:text-sm text-slate-500 mt-0.5">Manage USDT payment submissions</p>
        </div>
        <div className="flex items-center gap-2 sm:gap-3">
          {pendingCount > 0 && filter !== "pending" && (
            <span className="px-2.5 py-1 bg-amber-500/10 border border-amber-500/20 text-amber-400 text-xs font-semibold rounded-full">
              {pendingCount} Pending
            </span>
          )}
          <button onClick={() => fetchSubmissions(filter)} className="flex items-center gap-1.5 text-slate-400 hover:text-white hover:bg-white/5 rounded-lg px-2.5 py-1.5 transition-all" data-testid="refresh-payments">
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} /><span className="text-sm">Refresh</span>
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2">
        <Filter className="h-4 w-4 text-slate-500" />
        {["pending", "approved", "rejected", "all"].map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition ${
              filter === f
                ? "bg-blue-600/20 text-blue-400 border border-blue-500/30"
                : "bg-slate-900/50 text-slate-400 border border-slate-800/30 hover:bg-slate-800/50"
            }`}
            data-testid={`filter-${f}`}
          >
            {f.charAt(0).toUpperCase() + f.slice(1)}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-blue-500" />
        </div>
      ) : submissions.length === 0 ? (
        <Card className="bg-slate-900/60 border-slate-800/50">
          <CardContent className="py-12 text-center">
            <Coins className="h-8 w-8 text-slate-600 mx-auto mb-2" />
            <p className="text-sm text-slate-500">No {filter !== "all" ? filter : ""} payment submissions</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {submissions.map((sub) => (
            <Card key={sub.id} className="bg-slate-900/60 border-slate-800/50">
              <CardContent className="pt-4 pb-4">
                <div className="flex items-start justify-between gap-4">
                  {/* Left: Info */}
                  <div className="flex-1 min-w-0 space-y-2">
                    <div className="flex items-center gap-2 flex-wrap">
                      {getStatusBadge(sub.status)}
                      <span className="text-xs text-slate-500">{new Date(sub.created_at).toLocaleString()}</span>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-1.5 text-xs">
                      <div>
                        <span className="text-slate-500">User:</span>{" "}
                        <span className="text-slate-200 font-medium">{sub.user_name || sub.user_email}</span>
                      </div>
                      <div>
                        <span className="text-slate-500">Email:</span>{" "}
                        <span className="text-slate-300">{sub.user_email}</span>
                      </div>
                      <div>
                        <span className="text-slate-500">Plan:</span>{" "}
                        <span className="text-green-400 font-semibold">{sub.plan_label}</span>
                      </div>
                      <div>
                        <span className="text-slate-500">Amount:</span>{" "}
                        <span className="text-white font-mono font-medium">{sub.amount_usdt} USDT</span>
                      </div>
                      <div>
                        <span className="text-slate-500">Minutes:</span>{" "}
                        <span className="text-green-400 font-mono">{sub.credits_requested}</span>
                      </div>
                      <div className="col-span-2 sm:col-span-1">
                        <span className="text-slate-500">TXN ID:</span>{" "}
                        <span className="text-amber-400 font-mono text-[11px] break-all">{sub.txn_id || "—"}</span>
                      </div>
                    </div>

                    {/* Screenshot */}
                    {sub.screenshot_path && (
                      <div className="pt-1">
                        {screenshotUrls[sub.id] ? (
                          <img
                            src={screenshotUrls[sub.id]}
                            alt="Payment screenshot"
                            className="max-w-[300px] max-h-[200px] rounded-lg border border-slate-700 object-contain"
                          />
                        ) : (
                          <button
                            onClick={() => loadScreenshot(sub)}
                            className="inline-flex items-center gap-1.5 text-xs text-blue-400 hover:text-blue-300"
                            data-testid={`view-screenshot-${sub.id}`}
                          >
                            <Image className="h-3.5 w-3.5" /> View Screenshot
                          </button>
                        )}
                      </div>
                    )}

                    {/* Reject reason */}
                    {sub.status === "rejected" && sub.reject_reason && (
                      <p className="text-xs text-red-400 bg-red-500/5 px-2.5 py-1.5 rounded border border-red-500/10">
                        Reject reason: {sub.reject_reason}
                      </p>
                    )}

                    {sub.reviewed_at && (
                      <p className="text-[10px] text-slate-600">
                        Reviewed: {new Date(sub.reviewed_at).toLocaleString()}
                      </p>
                    )}
                  </div>

                  {/* Right: Actions */}
                  {sub.status === "pending" && (
                    <div className="flex flex-col gap-2 flex-shrink-0">
                      <Button
                        size="sm"
                        className="bg-green-600 hover:bg-green-500 text-xs h-8"
                        onClick={() => handleApprove(sub.id)}
                        disabled={!!actionLoading[sub.id]}
                        data-testid={`approve-${sub.id}`}
                      >
                        {actionLoading[sub.id] === "approve" ? (
                          <Loader2 className="h-3 w-3 animate-spin" />
                        ) : (
                          <>
                            <CheckCircle2 className="h-3 w-3 mr-1" /> Approve
                          </>
                        )}
                      </Button>
                      {!showRejectForm[sub.id] ? (
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-red-400 border-red-500/30 hover:bg-red-500/10 text-xs h-8"
                          onClick={() => setShowRejectForm((p) => ({ ...p, [sub.id]: true }))}
                          data-testid={`reject-btn-${sub.id}`}
                        >
                          <XCircle className="h-3 w-3 mr-1" /> Reject
                        </Button>
                      ) : (
                        <div className="space-y-1.5 w-32">
                          <Input
                            placeholder="Reason..."
                            value={rejectReasons[sub.id] || ""}
                            onChange={(e) => setRejectReasons((p) => ({ ...p, [sub.id]: e.target.value }))}
                            className="bg-slate-950 border-slate-800 text-slate-200 h-7 text-xs"
                            data-testid={`reject-reason-${sub.id}`}
                          />
                          <Button
                            size="sm"
                            className="w-full bg-red-600 hover:bg-red-500 text-xs h-7"
                            onClick={() => handleReject(sub.id)}
                            disabled={!!actionLoading[sub.id]}
                            data-testid={`confirm-reject-${sub.id}`}
                          >
                            {actionLoading[sub.id] === "reject" ? (
                              <Loader2 className="h-3 w-3 animate-spin" />
                            ) : (
                              "Confirm Reject"
                            )}
                          </Button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
